import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useParams } from 'react-router-dom';
import { usePriceGroup } from 'core/contexts/priceGroup';
import { CircularIndeterminate, Stepper } from '@utriper/core-ui';
import { Box, Button, Fade, Grid2, useMediaQuery, Zoom } from '@mui/material';
import PriceGroupDataForm from './components/PriceGroupDataForm';
import UnitsForm from './components/UnitsForm';
import SettingsForm from './components/SettingsForm';
import QuantityRangesForm from './components/QuantityRangesForm';
import PriceForm from './components/PriceForm';
import { CheckOutlined } from '@mui/icons-material';
import Title from '../components/Title';
import ExcelForm from './components/ExcelForm';

const PriceGroupForm = ({ activeStep, configType, CONFIG_TYPES }) => {
    switch (activeStep) {
        case 0:
            return <PriceGroupDataForm />;
        case 1:
            return <UnitsForm />;
        case 2:
            return <SettingsForm />;
        case 3:
            if (configType === CONFIG_TYPES.EXCEL) {
                return <ExcelForm />;
            }
            return <QuantityRangesForm />;
        case 4:
            return <PriceForm />;
        default:
            return null;
    }
};

const PriceGroupWizard = () => {
    const { t } = useTranslation();
    const translate = (key) => t(`price_group_wizard.${key}`);
    const { id } = useParams();
    const priceGroupTabId = parseInt(id);
    const history = useHistory();
    const isMobile = useMediaQuery((theme) => theme.breakpoints.down('sm'));
    const { activeStep, stepsWithIcons, configType, CONFIG_TYPES, loading, getInitialValues, isSuccess, setSuccess, resetWizard, newPriceGroupId, setNewPriceGroupId } = usePriceGroup();

    const title = translate('title');

    useEffect(() => {
        getInitialValues(priceGroupTabId, history);
        setSuccess(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [priceGroupTabId]);

    const buttonStyle = {
        width: '100%',
        py: '8px',
        borderRadius: '8px',
        textTransform: 'none',
    };

    const navigate = (route) => {
        history.push(route);
        resetWizard();
        setSuccess(false);
        setNewPriceGroupId(0);
    };

    // TODO: Move this to a new component
    if (isSuccess) {
        return (
            <Zoom in={isSuccess} timeout={500}>
                <Fade in={isSuccess} timeout={800}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', minHeight: '80dvh', textAlign: 'center', gap: 2, maxWidth: '500px', padding: '24px', justifySelf: 'center' }}>
                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: 64, height: 64, borderRadius: '100%', backgroundColor: '#50C878', boxShadow: '0px 0px 0px 8px #50C87840' }}>
                            <CheckOutlined sx={{ fontSize: 40, color: '#fff' }} />
                        </Box>
                        <Title title="🎉 ¡Grupo de precios creado con éxito!" description="Ya puedes vincular este grupo con tus experiencias o horarios" />

                        <Grid2 container direction="column" width="100%" justifyContent="center" alignItems="center" gap="8px" mt="16px">
                            <Button
                                variant="contained"
                                sx={{
                                    ...buttonStyle,
                                    backgroundColor: 'var(--color-utriper-blue)',
                                }}
                                onClick={() => {
                                    resetWizard();
                                    setSuccess(false);
                                }}
                            >
                                {translate('success.create_button')}
                            </Button>
                            <Grid2 container flexWrap="nowrap" width="100%" gap="16px">
                                <Button
                                    variant="outlined"
                                    sx={{
                                        ...buttonStyle,
                                        border: 'solid 1px #13131340',
                                        color: '#131313',
                                    }}
                                    onClick={() => navigate('/home')}
                                >
                                    {translate('success.back_button')}
                                </Button>
                                <Button
                                    variant="contained"
                                    sx={{
                                        ...buttonStyle,
                                        backgroundColor: 'var(--color-utriper-blue)',
                                    }}
                                    onClick={() => navigate(`/price-group/${newPriceGroupId}`)}
                                >
                                    {translate('success.details_button')}
                                </Button>
                            </Grid2>
                        </Grid2>
                    </Box>
                </Fade>
            </Zoom>
        );
    }

    if (loading) {
        return <CircularIndeterminate centered />;
    }

    return (
        <Box sx={{ position: 'relative', display: 'flex', justifyContent: 'center', gap: '2rem', width: '100%', minHeight: '100dvh', overflow: 'hidden' }}>
            {isMobile ? (
                <Stepper title={title} steps={stepsWithIcons} activeStep={activeStep} />
            ) : (
                <Box sx={{ maxWidth: '500px', width: '100%' }}>
                    <Stepper title={title} steps={stepsWithIcons} activeStep={activeStep} />
                </Box>
            )}

            <Box sx={{ flexGrow: isMobile ? 0 : 1, display: 'flex', justifyContent: 'center', alignItems: 'center', p: 2, pt: isMobile ? '6rem' : 2, maxWidth: '100%' }}>
                <PriceGroupForm activeStep={activeStep} configType={configType} CONFIG_TYPES={CONFIG_TYPES} />
            </Box>
        </Box>
    );
};

export default PriceGroupWizard;
